import React, {
  KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown, X } from "lucide-react";
import { PillPaletteToken } from "../config/palette";

export interface MultiSelectPillOption {
  id: string;
  value: string;
  label: string;
  colorKey?: string;
  disabled?: boolean;
}

export function MultiSelectPillField({
  value,
  options,
  onChange,
  ariaLabel,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  resolveColor,
  disabled = false,
  invalid = false,
}: {
  value: readonly string[];
  options: readonly MultiSelectPillOption[];
  onChange: (value: string[]) => void;
  ariaLabel: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
  resolveColor: (option: MultiSelectPillOption) => PillPaletteToken;
  disabled?: boolean;
  invalid?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const optionByValue = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const selectedOptions = value.map(
    (selectedValue) =>
      optionByValue.get(selectedValue) ?? {
        id: `selected-${selectedValue}`,
        value: selectedValue,
        label: selectedValue,
      },
  );
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const filteredOptions = options.filter((option) =>
    option.label.toLocaleLowerCase().includes(normalizedSearch),
  );

  const close = useCallback((restoreFocus = false) => {
    setOpen(false);
    setSearch("");
    setActiveIndex(0);
    if (restoreFocus) {
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, []);

  const show = () => {
    if (disabled) return;
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const handleMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close();
    };
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [close, open]);

  useEffect(() => {
    if (activeIndex >= filteredOptions.length) setActiveIndex(0);
  }, [activeIndex, filteredOptions.length]);

  const toggle = (option: MultiSelectPillOption) => {
    if (option.disabled) return;
    onChange(
      value.includes(option.value)
        ? value.filter((selectedValue) => selectedValue !== option.value)
        : [...value, option.value],
    );
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!filteredOptions.length) return;
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex(
        (current) =>
          (current + direction + filteredOptions.length) %
          filteredOptions.length,
      );
      return;
    }
    if (event.key === "Enter" && filteredOptions[activeIndex]) {
      event.preventDefault();
      toggle(filteredOptions[activeIndex]);
    }
  };

  const activeOption = filteredOptions[activeIndex];

  return (
    <div className="cp-multi-select" ref={rootRef}>
      <div
        className="cp-multi-select__field"
        data-open={open || undefined}
        data-disabled={disabled || undefined}
        aria-invalid={invalid || undefined}
        onClick={show}
      >
        <div className="cp-multi-select__values">
          {selectedOptions.length ? (
            selectedOptions.map((option) => (
              <span
                className="cp-value-pill cp-multi-select__pill"
                data-palette={resolveColor(option)}
                key={option.value}
              >
                <span>{option.label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${option.label}`}
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange(
                      value.filter(
                        (selectedValue) => selectedValue !== option.value,
                      ),
                    );
                  }}
                >
                  <X aria-hidden="true" />
                </button>
              </span>
            ))
          ) : (
            <span className="cp-multi-select__placeholder">{placeholder}</span>
          )}
        </div>
        <button
          ref={triggerRef}
          type="button"
          className="cp-multi-select__trigger"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          disabled={disabled}
          onClick={show}
          onKeyDown={(event) => {
            if (
              event.key === "ArrowDown" ||
              event.key === "Enter" ||
              event.key === " "
            ) {
              event.preventDefault();
              show();
            }
          }}
        >
          <ChevronDown aria-hidden="true" />
        </button>
      </div>

      {open ? (
        <div className="cp-multi-select__popover">
          <div className="cp-multi-select__search">
            <input
              ref={searchRef}
              type="search"
              role="combobox"
              aria-label={searchPlaceholder}
              aria-autocomplete="list"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={
                activeOption ? `${listboxId}-${activeOption.id}` : undefined
              }
              placeholder={searchPlaceholder}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
          <div
            id={listboxId}
            className="cp-multi-select__options"
            role="listbox"
            aria-label={`${ariaLabel} options`}
            aria-multiselectable="true"
          >
            {filteredOptions.length ? (
              filteredOptions.map((option, index) => {
                const selected = value.includes(option.value);
                return (
                  <label
                    id={`${listboxId}-${option.id}`}
                    role="option"
                    aria-selected={selected}
                    className="cp-multi-select__option"
                    data-active={index === activeIndex || undefined}
                    data-disabled={option.disabled || undefined}
                    key={option.id}
                    onMouseEnter={() => setActiveIndex(index)}
                  >
                    <input
                      type="checkbox"
                      checked={selected}
                      disabled={option.disabled}
                      aria-label={option.label}
                      onChange={() => toggle(option)}
                    />
                    <span
                      className="cp-value-pill"
                      data-palette={resolveColor(option)}
                    >
                      {option.label}
                    </span>
                    {option.disabled ? (
                      <span className="cp-multi-select__unavailable">
                        Unavailable — retained historical value
                      </span>
                    ) : null}
                  </label>
                );
              })
            ) : (
              <p className="cp-multi-select__empty">{emptyMessage}</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
